import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useRealTime } from "../context/RealTimeContext";
import { API_BASE_URL } from "../config";
import SalesExecutiveCalendar from "./SalesExecutiveCalendar";
import SalesExecutivePerformance from "./SalesExecutivePerformance";
import SalesmanPasswordModal from "../components/SalesmanPasswordModal";
import {
  Calendar,
  TrendingUp,
  Users,
  Target,
  Clock,
  BarChart3,
  Lightbulb,
  Bell,
  Lock,
  LogOut,
  Plus,
  Phone,
  Mail,
  DollarSign,
  ShoppingCart,
  ArrowUp,
  ArrowDown,
  Camera,
} from "lucide-react";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Button from "../components/ui/Button";
import toast from "react-hot-toast";

// Chart components for visualizations
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import { Bar, Radar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const SalesmanDashboard = () => {
  const { user, isAuthenticated, isSalesExecutive, logout } = useAuth();
  const navigate = useNavigate();
  const { subscribe, unsubscribe } = useRealTime();
  const [loading, setLoading] = useState(true);
  const [salesman, setSalesman] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [motivationalQuote, setMotivationalQuote] = useState("");
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [newActivity, setNewActivity] = useState({
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    time: "09:00",
    type: "customer_visit",
    priority: "medium",
    customerId: "",
  });
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationRef = React.useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  // Handle profile photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only image files (JPEG, PNG, GIF) are allowed");
      return;
    }

    try {
      setUploadingPhoto(true);

      const formData = new FormData();
      formData.append("photo", file);

      const response = await fetch(
        `${API_BASE_URL}/api/salesman/profile/photo`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to upload photo");
      }

      // Update local state
      setSalesman((prev) => ({
        ...prev,
        profilePhoto: data.profilePhoto,
      }));

      toast.success("Profile photo updated successfully!");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error(error.message || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Check authentication and role
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!isSalesExecutive) {
      toast.error(
        "Access denied. Only sales executives can access this dashboard."
      );
      navigate("/dashboard");
      return;
    }

    // Check if user must change password
    if (user?.mustChangePassword) {
      toast.info("Please change your password to continue.");
      setShowPasswordModal(true);
    }
  }, [isAuthenticated, isSalesExecutive, user, navigate]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiService.get("/notifications/list?limit=10");
      setNotifications(response || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    if (isAuthenticated && isSalesExecutive) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isSalesExecutive, fetchNotifications]);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showNotifications]);

  const fetchDashboardData = useCallback(async () => {
    try {
      console.log("🔄 Fetching salesman dashboard data...");
      const response = await apiService.get("/salesman/dashboard");
      console.log("✅ Dashboard data received:", response);
      setDashboardData(response);

      // Set personalized motivational quote
      const quotes = [
        "Success is not final, failure is not fatal: it is the courage to continue that counts.",
        "The only way to do great work is to love what you do.",
        "Your time is limited, don't waste it living someone else's life.",
        "The future belongs to those who believe in the beauty of their dreams.",
        "Don't watch the clock; do what it does. Keep going.",
      ];
      setMotivationalQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    } catch (error) {
      console.error("❌ Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    }
  }, []);

  const fetchAssignedCustomers = useCallback(async () => {
    try {
      setCustomersLoading(true);
      console.log("🔄 Fetching assigned customers...");
      const response = await apiService.get("/salesman/customers");
      console.log("✅ Assigned customers received:", response);
      setAssignedCustomers(response);
    } catch (error) {
      console.error("❌ Error fetching assigned customers:", error);
      toast.error("Failed to load assigned customers");
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load personalized data for the authenticated user
    const loadUserData = async () => {
      try {
        setLoading(true);

        // Load user profile data including photo
        console.log("🔍 Loading user profile data...");
        try {
          const profileData = await apiService.get("/salesman/profile");
          console.log("✅ Profile data received:", profileData);
          setSalesman(profileData);
        } catch (profileError) {
          console.error(
            "❌ Profile API failed, using user from context:",
            profileError
          );
          // Fallback to user data from AuthContext
          if (user) {
            console.log("✅ Using user data from context:", user);
            setSalesman(user);
          }
        }

        // Load dashboard data
        await fetchDashboardData();

        // Load assigned customers
        await fetchAssignedCustomers();

        setLoading(false);
      } catch (error) {
        console.error("❌ Error loading user data:", error);
        toast.error("Failed to load dashboard data");
        setLoading(false);
      }
    };

    // Load data if user is authenticated
    if (isAuthenticated) {
      loadUserData();
    }
  }, [isAuthenticated, fetchDashboardData, fetchAssignedCustomers, user]);

  // Subscribe to real-time updates
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      console.log("🔄 Salesman Dashboard received order update:", data);
      toast.success("New activity detected! Refreshing dashboard...");
      fetchDashboardData();
    };

    // Subscribe to order and invoice events
    subscribe("orderApproved", handleOrderUpdate, "SalesmanDashboard");
    subscribe("invoiceCreated", handleOrderUpdate, "SalesmanDashboard");
    subscribe("deliveryOrderCreated", handleOrderUpdate, "SalesmanDashboard");

    // Cleanup subscriptions
    return () => {
      unsubscribe("orderApproved", "SalesmanDashboard");
      unsubscribe("invoiceCreated", "SalesmanDashboard");
      unsubscribe("deliveryOrderCreated", "SalesmanDashboard");
    };
  }, [subscribe, unsubscribe, fetchDashboardData]);

  const getPerformanceChartData = () => {
    if (!dashboardData?.performance) {
      return {
        labels: ["Revenue", "Customers", "Orders"],
        datasets: [
          {
            label: "Current Month",
            data: [0, 0, 0],
            backgroundColor: "rgba(59, 130, 246, 0.8)",
            borderColor: "rgba(59, 130, 246, 1)",
            borderWidth: 2,
          },
          {
            label: "Last Month",
            data: [0, 0, 0],
            backgroundColor: "rgba(34, 197, 94, 0.8)",
            borderColor: "rgba(34, 197, 94, 1)",
            borderWidth: 2,
          },
        ],
      };
    }

    const { performance } = dashboardData;
    return {
      labels: ["Revenue", "Customers", "Orders"],
      datasets: [
        {
          label: "Current Month",
          data: [
            (performance.currentMonth.revenue /
              performance.currentMonth.target) *
              100,
            (performance.currentMonth.customers /
              performance.currentMonth.targetCustomers) *
              100,
            (performance.currentMonth.orders /
              performance.currentMonth.targetOrders) *
              100,
          ],
          backgroundColor: "rgba(59, 130, 246, 0.8)",
          borderColor: "rgba(59, 130, 246, 1)",
          borderWidth: 2,
        },
        {
          label: "Last Month",
          data: [
            (performance.lastMonth.revenue / performance.currentMonth.target) *
              100,
            (performance.lastMonth.customers /
              performance.currentMonth.targetCustomers) *
              100,
            (performance.lastMonth.orders /
              performance.currentMonth.targetOrders) *
              100,
          ],
          backgroundColor: "rgba(34, 197, 94, 0.8)",
          borderColor: "rgba(34, 197, 94, 1)",
          borderWidth: 2,
        },
      ],
    };
  };

  const getCustomerRadarData = () => {
    if (!dashboardData?.customerAnalysis) {
      return {
        labels: [
          "Active Customers",
          "Slow Customers",
          "Redundant Customers",
          "High Value",
          "Regular",
          "At Risk",
        ],
        datasets: [
          {
            label: "Customer Distribution",
            data: [0, 0, 0, 0, 0, 0],
            backgroundColor: "rgba(59, 130, 246, 0.2)",
            borderColor: "rgba(59, 130, 246, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(59, 130, 246, 1)",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "rgba(59, 130, 246, 1)",
          },
        ],
      };
    }

    const { customerAnalysis } = dashboardData;
    return {
      labels: [
        "Active Customers",
        "Slow Customers",
        "Redundant Customers",
        "High Value",
        "Regular",
        "At Risk",
      ],
      datasets: [
        {
          label: "Customer Distribution",
          data: [
            customerAnalysis.active.length,
            customerAnalysis.slow.length,
            customerAnalysis.redundant.length,
            customerAnalysis.active.filter((c) => c.status === "high_value")
              .length,
            customerAnalysis.active.filter((c) => c.status === "regular")
              .length,
            customerAnalysis.slow.filter((c) => c.status === "at_risk").length,
          ],
          backgroundColor: "rgba(59, 130, 246, 0.2)",
          borderColor: "rgba(59, 130, 246, 1)",
          borderWidth: 2,
          pointBackgroundColor: "rgba(59, 130, 246, 1)",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "rgba(59, 130, 246, 1)",
        },
      ],
    };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-100";
      case "medium":
        return "text-yellow-600 bg-yellow-100";
      case "low":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "high_value":
        return "text-green-600 bg-green-100";
      case "regular":
        return "text-blue-600 bg-blue-100";
      case "growing":
        return "text-purple-600 bg-purple-100";
      case "declining":
        return "text-yellow-600 bg-yellow-100";
      case "at_risk":
        return "text-orange-600 bg-orange-100";
      case "inactive":
        return "text-gray-600 bg-gray-100";
      case "lost":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const handleAddActivity = () => {
    if (!newActivity.title || !newActivity.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    const activity = {
      id: `manual-${Date.now()}`,
      ...newActivity,
      status: "scheduled",
      type: "manual_activity",
    };

    // Update dashboard data with new activity
    setDashboardData((prevData) => ({
      ...prevData,
      activities: [activity, ...(prevData?.activities || [])],
    }));

    setNewActivity({
      title: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
      time: "09:00",
      type: "customer_visit",
      priority: "medium",
      customerId: "",
    });

    setShowActivityModal(false);
    toast.success("Activity added successfully!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <svg
                  className="w-7 h-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Salesman Dashboard
              </h1>
              <p className="mt-1 text-gray-600">
                Welcome back, {salesman?.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {Array.isArray(notifications) &&
                  notifications.filter((n) => n.unread).length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Notifications
                      </h3>
                      {Array.isArray(notifications) &&
                        notifications.filter((n) => n.unread).length > 0 && (
                          <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                            {notifications.filter((n) => n.unread).length} new
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {!Array.isArray(notifications) ||
                    notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                            notification.unread ? "bg-blue-50" : ""
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                notification.unread
                                  ? "bg-blue-100"
                                  : "bg-gray-100"
                              }`}
                            >
                              <Bell
                                className={`w-4 h-4 ${
                                  notification.unread
                                    ? "text-blue-600"
                                    : "text-gray-600"
                                }`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(
                                  notification.createdAt
                                ).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {Array.isArray(notifications) && notifications.length > 0 && (
                    <div className="p-3 border-t border-gray-200 text-center">
                      <button
                        onClick={() => navigate("/admin/notifications")}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View All Notifications
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowPasswordModal(true)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Change Password"
            >
              <Lock className="w-5 h-5" />
            </button>
            <button
              onClick={logout}
              className="p-2 text-red-400 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Password Change Modal */}
        <SalesmanPasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
        />

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: "dashboard", label: "Dashboard", icon: BarChart3 },
                { id: "calendar", label: "Activity Calendar", icon: Calendar },
                { id: "performance", label: "Performance", icon: TrendingUp },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "dashboard" && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Profile Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <div className="relative w-20 h-20 rounded-full mx-auto mb-4 border-4 border-blue-50 bg-gray-100 overflow-hidden group">
                    <img
                      src={
                        salesman?.profilePhoto
                          ? `${API_BASE_URL}${salesman.profilePhoto}`
                          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              salesman?.name || "User"
                            )}&background=3B82F6&color=fff&size=128`
                      }
                      alt={salesman?.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Upload overlay */}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
                    >
                      {uploadingPhoto ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                      ) : (
                        <Camera className="w-6 h-6 text-white" />
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {salesman?.name}
                  </h3>
                  <p className="text-sm text-gray-500">{salesman?.territory}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {salesman?.experience} experience
                  </p>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-center space-x-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{salesman?.email}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{salesman?.phone}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Motivational Quote */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white shadow-md">
                <div className="flex items-start space-x-3">
                  <Lightbulb className="w-6 h-6 text-yellow-300 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-2">Today's Motivation</h4>
                    <p className="text-sm text-blue-100 leading-relaxed italic">
                      "{motivationalQuote}"
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="font-semibold text-gray-900 mb-4">
                  Target Achievement
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">
                        Revenue Achievement
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          dashboardData?.performance?.currentMonth
                            .revenueAchievement >= 100
                            ? "text-green-600"
                            : dashboardData?.performance?.currentMonth
                                .revenueAchievement >= 80
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {dashboardData?.performance?.currentMonth.revenueAchievement?.toFixed(
                          1
                        ) || "0"}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          dashboardData?.performance?.currentMonth
                            .revenueAchievement >= 100
                            ? "bg-green-600"
                            : dashboardData?.performance?.currentMonth
                                .revenueAchievement >= 80
                            ? "bg-yellow-600"
                            : "bg-red-600"
                        }`}
                        style={{
                          width: `${Math.min(
                            dashboardData?.performance?.currentMonth
                              .revenueAchievement || 0,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>
                        ₹
                        {dashboardData?.performance?.currentMonth.revenue?.toLocaleString() ||
                          "0"}
                      </span>
                      <span>
                        Target: ₹
                        {dashboardData?.performance?.currentMonth.target?.toLocaleString() ||
                          "0"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">
                        Order Achievement
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          dashboardData?.performance?.currentMonth
                            .orderAchievement >= 100
                            ? "text-green-600"
                            : dashboardData?.performance?.currentMonth
                                .orderAchievement >= 80
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {dashboardData?.performance?.currentMonth.orderAchievement?.toFixed(
                          1
                        ) || "0"}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          dashboardData?.performance?.currentMonth
                            .orderAchievement >= 100
                            ? "bg-green-600"
                            : dashboardData?.performance?.currentMonth
                                .orderAchievement >= 80
                            ? "bg-yellow-600"
                            : "bg-red-600"
                        }`}
                        style={{
                          width: `${Math.min(
                            dashboardData?.performance?.currentMonth
                              .orderAchievement || 0,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>
                        {dashboardData?.performance?.currentMonth.orders || 0}{" "}
                        orders
                      </span>
                      <span>
                        Target:{" "}
                        {dashboardData?.performance?.currentMonth
                          .targetOrders || 0}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">
                        Customer Achievement
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          dashboardData?.performance?.currentMonth
                            .customerAchievement >= 100
                            ? "text-green-600"
                            : dashboardData?.performance?.currentMonth
                                .customerAchievement >= 80
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {dashboardData?.performance?.currentMonth.customerAchievement?.toFixed(
                          1
                        ) || "0"}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          dashboardData?.performance?.currentMonth
                            .customerAchievement >= 100
                            ? "bg-green-600"
                            : dashboardData?.performance?.currentMonth
                                .customerAchievement >= 80
                            ? "bg-yellow-600"
                            : "bg-red-600"
                        }`}
                        style={{
                          width: `${Math.min(
                            dashboardData?.performance?.currentMonth
                              .customerAchievement || 0,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>
                        {dashboardData?.performance?.currentMonth.customers ||
                          0}{" "}
                        customers
                      </span>
                      <span>
                        Target:{" "}
                        {dashboardData?.performance?.currentMonth
                          .targetCustomers || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Performance Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Revenue</p>
                        <p className="text-lg font-semibold text-gray-900">
                          ₹
                          {dashboardData?.performance?.currentMonth.revenue?.toLocaleString() ||
                            "0"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-1">
                        <ArrowUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">+8.5%</span>
                      </div>
                      <p className="text-xs text-gray-500">vs last month</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${
                          (dashboardData?.performance?.currentMonth.revenue /
                            dashboardData?.performance?.currentMonth.target) *
                            100 || 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Customers</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {dashboardData?.performance?.currentMonth.customers ||
                            0}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-1">
                        <ArrowDown className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">-2.1%</span>
                      </div>
                      <p className="text-xs text-gray-500">vs last month</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{
                        width: `${
                          (dashboardData?.performance?.currentMonth.customers /
                            dashboardData?.performance?.currentMonth
                              .targetCustomers) *
                            100 || 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Orders</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {dashboardData?.performance?.currentMonth.orders || 0}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-1">
                        <ArrowUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">+12.3%</span>
                      </div>
                      <p className="text-xs text-gray-500">vs last month</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{
                        width: `${
                          (dashboardData?.performance?.currentMonth.orders /
                            dashboardData?.performance?.currentMonth
                              .targetOrders) *
                            100 || 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    Performance Comparison
                  </h4>
                  <div className="h-64">
                    <Bar
                      data={getPerformanceChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: "top" },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: 100,
                            ticks: {
                              callback: function (value) {
                                return value + "%";
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    Customer Analysis
                  </h4>
                  <div className="h-64">
                    <Radar
                      data={getCustomerRadarData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                        },
                        scales: {
                          r: {
                            beginAtZero: true,
                            ticks: {
                              stepSize: 1,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Market News & Customer Analysis */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Market Intelligence */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">
                      Market Intelligence
                    </h4>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                      View All
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="w-8 h-8 text-blue-600" />
                      </div>
                      <h5 className="font-medium text-gray-900 mb-2">
                        Market Intelligence Coming Soon
                      </h5>
                      <p className="text-sm text-gray-600">
                        Real-time market insights and competitor analysis will
                        be available here.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Customer Analysis */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">
                      Customer Analysis
                    </h4>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                      View Details
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-gray-900 text-sm mb-2">
                        Active Customers (
                        {dashboardData?.customerAnalysis?.active?.length || 0})
                      </h5>
                      <div className="space-y-2">
                        {dashboardData?.customerAnalysis?.active
                          ?.slice(0, 3)
                          .map((customer) => (
                            <div
                              key={customer.id}
                              className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-100"
                            >
                              <div>
                                <p className="font-medium text-sm text-gray-900">
                                  {customer.name}
                                </p>
                                <p className="text-xs text-gray-600">
                                  ₹{(customer.revenue || 0).toLocaleString()}
                                </p>
                              </div>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                  customer.status
                                )}`}
                              >
                                {customer.status.replace("_", " ")}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-900 text-sm mb-2">
                        At Risk (
                        {dashboardData?.customerAnalysis?.slow?.length || 0})
                      </h5>
                      <div className="space-y-2">
                        {dashboardData?.customerAnalysis?.slow
                          ?.slice(0, 2)
                          .map((customer) => (
                            <div
                              key={customer.id}
                              className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg border border-yellow-100"
                            >
                              <div>
                                <p className="font-medium text-sm text-gray-900">
                                  {customer.name}
                                </p>
                                <p className="text-xs text-gray-600">
                                  ₹{(customer.revenue || 0).toLocaleString()}
                                </p>
                              </div>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                  customer.status
                                )}`}
                              >
                                {customer.status.replace("_", " ")}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Suggested Tasks & Calendar */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Suggested Tasks */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">
                      AI Suggested Tasks
                    </h4>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                      View All
                    </button>
                  </div>
                  <div className="space-y-4">
                    {dashboardData?.suggestedTasks?.map((task) => (
                      <div
                        key={task.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-medium text-gray-900 text-sm">
                            {task.title}
                          </h5>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(
                              task.priority
                            )}`}
                          >
                            {task.priority}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          {task.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>{task.estimatedTime || "N/A"}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <DollarSign className="w-3 h-3" />
                              <span>
                                ₹{(task.potentialRevenue || 0).toLocaleString()}
                              </span>
                            </span>
                          </div>
                          <button className="text-blue-600 hover:text-blue-700 text-xs font-medium">
                            Add to Calendar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity Calendar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">
                      Activity Calendar
                    </h4>
                    <button
                      onClick={() => setShowActivityModal(true)}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Activity</span>
                    </button>
                  </div>
                  <div className="space-y-3">
                    {dashboardData?.activities?.slice(0, 4).map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg"
                      >
                        <div
                          className={`w-3 h-3 rounded-full ${
                            activity.priority === "high"
                              ? "bg-red-500"
                              : activity.priority === "medium"
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          }`}
                        ></div>
                        <div className="flex-1">
                          <h5 className="font-medium text-sm text-gray-900">
                            {activity.title}
                          </h5>
                          <p className="text-xs text-gray-600">
                            {activity.date} at {activity.time}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(
                            activity.priority
                          )}`}
                        >
                          {activity.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && <SalesExecutiveCalendar />}

        {/* Performance Tab */}
        {activeTab === "performance" && <SalesExecutivePerformance />}

        {/* Assigned Customers Section */}
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  My Assigned Customers
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and track your assigned customers
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {assignedCustomers.length} customer
                  {assignedCustomers.length !== 1 ? "s" : ""}
                </span>
                <button
                  onClick={fetchAssignedCustomers}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Refresh
                </button>
              </div>
            </div>

            {customersLoading ? (
              <div className="flex justify-center items-center h-32">
                <LoadingSpinner size="md" />
              </div>
            ) : assignedCustomers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assignedCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {customer.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {customer.businessName || "No Business Name"}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          customer.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : customer.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {customer.status}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-xs text-gray-600">
                        <Mail className="w-3 h-3 mr-2" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                      {customer.contactMobile && (
                        <div className="flex items-center text-xs text-gray-600">
                          <Phone className="w-3 h-3 mr-2" />
                          <span>{customer.contactMobile}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Orders: {customer.orderCount || 0}</span>
                        <span>
                          Revenue: ₹
                          {(customer.totalRevenue || 0).toLocaleString()}
                        </span>
                      </div>
                      {customer.lastOrderDate && (
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="w-3 h-3 mr-2" />
                          <span>
                            Last Order:{" "}
                            {new Date(
                              customer.lastOrderDate
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2">
                        <button className="text-blue-600 hover:text-blue-700 text-xs font-medium">
                          View Details
                        </button>
                        <button className="text-green-600 hover:text-green-700 text-xs font-medium">
                          Contact
                        </button>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Phone className="w-3 h-3" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Mail className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  No Customers Assigned
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  You don't have any customers assigned to you yet. Contact your
                  manager to get customer assignments.
                </p>
                <button
                  onClick={fetchAssignedCustomers}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Add Activity Modal */}
        {showActivityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add New Activity
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newActivity.title}
                    onChange={(e) =>
                      setNewActivity({ ...newActivity, title: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Activity title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newActivity.description}
                    onChange={(e) =>
                      setNewActivity({
                        ...newActivity,
                        description: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Activity description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={newActivity.date}
                      onChange={(e) =>
                        setNewActivity({ ...newActivity, date: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      value={newActivity.time}
                      onChange={(e) =>
                        setNewActivity({ ...newActivity, time: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={newActivity.type}
                      onChange={(e) =>
                        setNewActivity({ ...newActivity, type: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="customer_visit">Customer Visit</option>
                      <option value="phone_call">Phone Call</option>
                      <option value="email">Email</option>
                      <option value="research">Research</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={newActivity.priority}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          priority: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowActivityModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddActivity}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Activity
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesmanDashboard;
